import { useCallback, useEffect, useSyncExternalStore } from 'react';

/**
 * Colour-palette theme — the user-controlled surface of the
 * light/dark toggle.
 *
 * `system` means "follow the OS `prefers-color-scheme` value and
 * don't override it". `light` / `dark` mean "force this palette
 * regardless of OS". The resolved value is what the UI actually
 * reads at any given moment.
 *
 * We persist the `preference` (system/light/dark) to localStorage;
 * the `resolved` value is derived at render time from the preference
 * plus the current OS pref. This keeps the toggle stateless and
 * avoids re-rendering on every OS pref change when the user has
 * explicitly chosen a side.
 */
export type ThemePreference = 'system' | 'light' | 'dark';
export type ThemeResolved = 'light' | 'dark';

/**
 * The OS preference at the moment of read. Cached so a render
 * doesn't recompute it; the change subscription below invalidates
 * the cache when the OS flips while the user is on `system`.
 *
 * `null` is a valid value (the API can return `no-preference`),
 * which we treat as "use light" — the historical default before
 * dark mode existed.
 */
type SystemPref = 'light' | 'dark' | 'no-preference';

const STORAGE_KEY = 'osd.theme.preference';
const HTML_ATTR = 'data-osd-theme';
const VALID_PREFS: ReadonlyArray<ThemePreference> = ['system', 'light', 'dark'];

/**
 * Read the current OS preference. Browser-only — guarded with a
 * typeof check so SSR / unit tests can call it without throwing.
 */
function readSystemPref(): SystemPref {
  if (typeof window === 'undefined' || !window.matchMedia) return 'no-preference';
  const mql = window.matchMedia('(prefers-color-scheme: dark)');
  return mql.matches ? 'dark' : 'light';
}

/**
 * Resolve a preference against the current OS preference.
 *
 *   system + os:dark   → dark
 *   system + os:light  → light
 *   system + no-pref   → light  (legacy default)
 *   light              → light
 *   dark               → dark
 */
function resolve(preference: ThemePreference, systemPref: SystemPref): ThemeResolved {
  if (preference === 'light') return 'light';
  if (preference === 'dark') return 'dark';
  // preference === 'system'
  return systemPref === 'dark' ? 'dark' : 'light';
}

/**
 * Apply the `data-osd-theme` attribute on `<html>`. This is the
 * lever the compiled `@osd/ui` CSS watches — see
 * `packages/ui/src/styles/_tokens.scss` §"Dark theme override".
 *
 *   preference: 'dark'   → <html data-osd-theme="dark">    (explicit dark wins)
 *   preference: 'light'  → <html data-osd-theme="light">   (explicit light wins)
 *   preference: 'system' → no attribute (let the @media query decide)
 *
 * The 'light' case MUST set the attribute (not remove it) — the
 * media-query guard is `:root:not([data-osd-theme='light'])`, and
 * `:root:not(X)` evaluates TRUE when the attribute is absent. If
 * we removed the attribute on a light pin, the dark media-query
 * block would still match and the page would stay dark whenever
 * the OS was dark — the user's "pin light" choice would silently
 * lose to the OS preference.
 */
function applyToDom(preference: ThemePreference): void {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  if (preference === 'system') {
    root.removeAttribute(HTML_ATTR);
  } else {
    root.setAttribute(HTML_ATTR, preference);
  }
}

/**
 * Read the persisted preference. Returns `'system'` when nothing is
 * stored or the stored value is unrecognised — that's the
 * back-compat default; users who never opened a browser in dark
 * mode continue to follow their OS.
 */
function readStoredPreference(): ThemePreference {
  if (typeof window === 'undefined') return 'system';
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw === null) return 'system';
    return (VALID_PREFS as ReadonlyArray<string>).includes(raw)
      ? (raw as ThemePreference)
      : 'system';
  } catch {
    // localStorage throws in Safari private mode and in some
    // sandboxed contexts — treat as "no preference".
    return 'system';
  }
}

function writeStoredPreference(preference: ThemePreference): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, preference);
  } catch {
    // Same as above — swallow. The DOM-level attribute is the
    // source of truth for the current page lifetime; persistence
    // is best-effort.
  }
}

/**
 * Tiny pub-sub for the preference + system-pref pair. Using a
 * module-scoped store (instead of `useSyncExternalStore` over a
 * React context) avoids forcing every consumer into a provider
 * while still giving them reactive re-renders.
 */
type Listener = () => void;

function createThemeStore(): {
  getSnapshot(): { preference: ThemePreference; systemPref: SystemPref };
  subscribe(listener: Listener): () => void;
  setPreference(preference: ThemePreference): void;
} {
  let state = {
    preference: readStoredPreference(),
    systemPref: readSystemPref(),
  };
  const listeners = new Set<Listener>();

  function emit(): void {
    for (const listener of listeners) listener();
  }

  function attachSystemListener(): () => void {
    if (typeof window === 'undefined' || !window.matchMedia) return () => {};
    const mql = window.matchMedia('(prefers-color-scheme: dark)');
    // `addEventListener` is the modern API; older Safari only had
    // `addListener`. We try the modern one first and fall back.
    const handler = (e: MediaQueryListEvent): void => {
      state = { ...state, systemPref: e.matches ? 'dark' : 'light' };
      emit();
    };
    if (mql.addEventListener) {
      mql.addEventListener('change', handler);
      return () => mql.removeEventListener('change', handler);
    }
    // Legacy Safari (< 14) only exposes the deprecated
    // addListener API. The OSD supported-targets list (Chrome,
    // Firefox, Safari 14+) all support the modern API above, so
    // this fallback is intentionally absent — the deprecation
    // warning would force us to suppress lint just to support a
    // browser that no longer ships.
    return () => {};
  }

  // Subscribe to OS changes exactly once for the module's lifetime.
  // Returns an unsubscribe in case the test environment wants to
  // detach between tests, but in production this is a no-op.
  const detach = attachSystemListener();

  return {
    getSnapshot(): { preference: ThemePreference; systemPref: SystemPref } {
      return state;
    },
    subscribe(listener: Listener): () => void {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    setPreference(preference: ThemePreference): void {
      if (!VALID_PREFS.includes(preference)) return;
      if (preference === state.preference) return;
      state = { ...state, preference };
      writeStoredPreference(preference);
      emit();
    },
    // Exposed for tests only; in prod the OS listener detaches on
    // page unload automatically.
    _detach: detach,
  } as ReturnType<typeof createThemeStore> & { _detach: () => void };
}

const store = createThemeStore();

/**
 * The `useTheme()` contract — every public method on this object is
 * stable across renders (same reference), so consumers can put them
 * in dependency arrays without retriggering effects.
 */
export interface UseThemeResult {
  /** What the user explicitly chose. `'system'` = follow OS. */
  readonly preference: ThemePreference;
  /** What the UI should actually render right now. Never `'system'`. */
  readonly resolved: ThemeResolved;
  /** Pin to light, ignoring OS. Persists to localStorage. */
  readonly setLight: () => void;
  /** Pin to dark, ignoring OS. Persists to localStorage. */
  readonly setDark: () => void;
  /** Clear any pin and follow the OS preference. Persists to localStorage. */
  readonly reset: () => void;
  /**
   * Toggle between light and dark. When the current preference is
   * `'system'`, the toggle pins to the OPPOSITE of the resolved
   * value — so a user on a dark OS who clicks the toggle sees
   * light, not stays dark. After the first click, subsequent
   * clicks flip light ↔ dark directly.
   */
  readonly toggle: () => void;
}

/**
 * `useTheme()` — single source of truth for the colour palette.
 *
 *   - Reads the persisted preference on first render.
 *   - Mirrors the resolved value to `<html data-osd-theme>` so the
 *     compiled `@osd/ui` CSS can do its thing.
 *   - Re-renders when the OS preference changes while the user is
 *     on `'system'` (so a user whose OS flips at sunset sees the
 *     change live without a reload).
 *
 * @example
 *   const { resolved, toggle } = useTheme();
 *   return <ThemeToggle resolved={resolved} onClick={toggle} />;
 */
export function useTheme(): UseThemeResult {
  const snapshot = useSyncExternalStore(store.subscribe, store.getSnapshot, () => ({
    preference: 'system' as ThemePreference,
    systemPref: 'no-preference' as SystemPref,
  }));

  const resolved = resolve(snapshot.preference, snapshot.systemPref);

  // Mirror preference → <html data-osd-theme>. We do this in an
  // effect (not during render) because DOM writes must not happen
  // during render — React 18 strict-mode double-render would write
  // the attribute twice in dev. The effect runs once per preference
  // change and is a no-op when the attribute is already correct.
  //
  // Note: we key on `preference` (the user's pinned choice), not
  // `resolved` (which also depends on the live OS pref). For
  // preference='system' we WANT the attribute removed so the
  // @media (prefers-color-scheme: dark) CSS path owns the palette;
  // for preference='light'|'dark' we want the explicit attribute
  // so the media-query guard `:root:not([data-osd-theme='light'])`
  // yields correctly.
  useEffect(() => {
    applyToDom(snapshot.preference);
  }, [snapshot.preference]);

  const setLight = useCallback(() => store.setPreference('light'), []);
  const setDark = useCallback(() => store.setPreference('dark'), []);
  const reset = useCallback(() => store.setPreference('system'), []);
  const toggle = useCallback(() => {
    // Always flip the resolved value — when the user is on
    // `system`, that means pin to the opposite of what the OS is
    // showing (otherwise the toggle would feel broken — click and
    // nothing changes). After the first click, the user is pinned
    // to a side and subsequent clicks flip light ↔ dark directly.
    const next: ThemePreference = resolved === 'dark' ? 'light' : 'dark';
    store.setPreference(next);
  }, [resolved]);

  return { preference: snapshot.preference, resolved, setLight, setDark, reset, toggle };
}

/**
 * Reset the `data-osd-theme` attribute on `<html>`. Exported for
 * tests + for an explicit "tear-down" use case (none today).
 */
export function _resetThemeDom(): void {
  if (typeof document === 'undefined') return;
  document.documentElement.removeAttribute(HTML_ATTR);
}
